import { AlertTriangle, Info } from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import type { CrossFlagRmaVerdict, CrossFlagRow } from "@busright/data-contract";
import { RAG } from "@busright/ui";

import { ChartCard } from "@/components/ChartCard";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { formatInt, formatScore, formatUsd, formatUsdCompact } from "@/lib/format";

// AR × Hardware risk "double-flag" panel for the Finance surface. A double-flag is a district that
// carries open AR AND trips the warehouse hw_trouble_state_flag. Rows drill to the Districts surface
// (there is no per-district route yet). The RMA verdict is shown verbatim — RMAs are single-tenant
// with no district key, so an RMA×health flag is deliberately NOT fabricated. The HW-trouble flag IS
// a genuine health signal, so RAG.WARN is the correct (and only) RAG use on this otherwise-$ surface.

export interface FinDoubleFlagSectionProps {
  readonly doubleFlags: readonly CrossFlagRow[];
  readonly totalOpenArUsd: number | null;
  readonly doubleFlagArUsd: number;
  readonly rma: CrossFlagRmaVerdict | null;
  readonly loading: boolean;
  readonly isError: boolean;
  readonly errorMessage: string;
}

const RMA_FALLBACK =
  "RMA × health is not produced: RMAs are single-tenant with no district key, so they cannot be attributed.";

export const FinDoubleFlagSection = ({
  doubleFlags,
  totalOpenArUsd,
  doubleFlagArUsd,
  rma,
  loading,
  isError,
  errorMessage,
}: FinDoubleFlagSectionProps): ReactNode => {
  const navigate = useNavigate();

  return (
    <div style={{ marginTop: 16 }}>
      <ChartCard
        title="AR × Hardware risk — double-flags"
        subtitle="Districts that carry open AR AND trip the warehouse hw_trouble_state_flag. Click a row to open Districts. ($ = open AR, not customer revenue.)"
        source="invoices.balance ⋈ rpt_hw_device_latest.hw_trouble_state_flag"
        loading={loading}
        action={
          totalOpenArUsd !== null ? (
            <span className="card-source br-tabular-nums">
              {formatUsdCompact(doubleFlagArUsd)} of {formatUsdCompact(totalOpenArUsd)} open AR
            </span>
          ) : undefined
        }
      >
        {isError ? (
          <EmptyState
            title="Couldn't load cross-flags"
            message={errorMessage}
            icon={AlertTriangle}
          />
        ) : (
          <DataTable<CrossFlagRow>
            rows={doubleFlags}
            rowKey={(r) => r.districtId}
            onRowClick={() => navigate("/districts")}
            emptyTitle="No double-flags"
            emptyMessage="No district currently carries both open AR and a hardware-trouble flag."
            columns={[
              {
                key: "district",
                header: "District",
                render: (r) => (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span>{r.districtName ?? r.recipient ?? "—"}</span>
                    <span className="mono" style={{ fontSize: 11, color: "var(--br-text-muted)" }}>
                      {r.state ?? "—"}
                    </span>
                  </div>
                ),
              },
              {
                key: "ar",
                header: "Open AR",
                align: "right",
                render: (r) => (
                  <span className="mono br-tabular-nums">{formatUsd(r.openArUsd)}</span>
                ),
              },
              {
                key: "devices",
                header: "Devices",
                align: "right",
                render: (r) => (
                  <span className="mono br-tabular-nums">
                    {r.deviceCount === null ? "—" : formatInt(r.deviceCount)}
                  </span>
                ),
              },
              {
                key: "health",
                header: "Avg health",
                align: "right",
                render: (r) => (
                  <span className="mono br-tabular-nums">
                    {formatScore(r.avgCompositeHealthScore)}
                  </span>
                ),
              },
              {
                key: "risk",
                header: "HW trouble flag",
                render: (r) => (
                  <span className="mono" style={{ color: RAG.WARN }}>
                    {r.deviceRisk ? "flagged" : "—"}
                  </span>
                ),
              },
            ]}
          />
        )}
        <div
          style={{
            marginTop: 12,
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            padding: "10px 12px",
            fontSize: 12,
            color: "var(--br-text-muted)",
            border: "1px solid var(--br-border)",
            borderRadius: 8,
            background: "var(--br-surface-2)",
          }}
        >
          <Info size={14} aria-hidden style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{rma?.reason ?? RMA_FALLBACK}</span>
        </div>
      </ChartCard>
    </div>
  );
};
